/**
 * Help & FAQ content. Each entry is searchable by question, answer, category
 * and keywords. Grouped by `category` in the Help drawer.
 *
 * Keep answers concise and product-accurate; this is end-user help, not docs.
 */

export const FAQ_CATEGORIES = [
  "Getting Started",
  "Subscriptions & Plans",
  "Modules & Sections",
  "Fields Glossary",
  "Common Flows",
  "Workflows",
  "Account & Access",
];

const faqData = [
  // ── Getting Started ──────────────────────────────────────────────────
  {
    id: "gs-1",
    category: "Getting Started",
    question: "What is this application?",
    answer:
      "It is a multi-company ERP covering Sales, Purchasing, Inventory, Accounting, HR & Payroll, Service, CRM, Manufacturing and automation Workflows. The modules you see depend on your subscription plan.",
    keywords: ["erp", "overview", "about", "what is"],
  },
  {
    id: "gs-2",
    category: "Getting Started",
    question: "Why can't I see a module in the sidebar?",
    answer:
      "The sidebar only shows modules your subscription plan includes (and that your role can access). For example, Manufacturing requires an ERP Standard/Pro plan and CRM requires ERP Pro. Ask an admin to upgrade the plan or grant your role access.",
    keywords: ["missing module", "sidebar", "hidden", "access denied", "no permission"],
  },
  {
    id: "gs-3",
    category: "Getting Started",
    question: "How do I switch between companies/branches?",
    answer:
      "Click your name/branch in the top-right header (or the user menu → Switch Branch), then pick a branch. Your data, vouchers and reports are scoped to the active branch.",
    keywords: ["branch", "company", "switch", "change branch", "multi company"],
  },

  // ── Subscriptions & Plans ────────────────────────────────────────────
  {
    id: "sub-1",
    category: "Subscriptions & Plans",
    question: "What subscription products and tiers exist?",
    answer:
      "Products: ForthERP, PharmaERP, ForthBooks, PharmaBooks, HRPayroll, and SuperERP (internal). Most products come in Basic, Standard and Pro tiers. Your plan is shown as e.g. 'ForthERP_Pro'.",
    keywords: ["plans", "tiers", "basic", "standard", "pro", "products", "forthERP", "pharmaERP"],
  },
  {
    id: "sub-2",
    category: "Subscriptions & Plans",
    question: "What does each ERP tier include?",
    answer:
      "ERP Basic includes the core 12 modules (Dashboard, Sales, Quotation, Order, Purchase, POS, Inventory, Accounts, Reports, HR & Payroll, Service, Settings). Standard adds Manufacturing. Pro adds CRM on top of Standard.",
    keywords: ["tier", "difference", "manufacturing", "crm", "basic vs pro", "what included"],
  },
  {
    id: "sub-3",
    category: "Subscriptions & Plans",
    question: "What do ForthBooks / PharmaBooks plans include?",
    answer:
      "The accounting-focused editions grant the same module set across Basic/Standard/Pro (Accounts, Dashboard, HR & Payroll, Inventory, Order, POS, Purchase, Quotation, Reports, Sales, Service, Settings). Tiers differ by commercial limits, not modules.",
    keywords: ["forthbooks", "pharmabooks", "accounting edition", "books"],
  },
  {
    id: "sub-4",
    category: "Subscriptions & Plans",
    question: "What does the HRPayroll plan include?",
    answer:
      "The standalone HRPayroll product includes only HR & Payroll and Settings — employees, attendance, leave and payroll. It does not include Sales/Purchase/Inventory.",
    keywords: ["hrpayroll", "hr only", "payroll plan"],
  },
  {
    id: "sub-5",
    category: "Subscriptions & Plans",
    question: "How many workflows can my company create?",
    answer:
      "Workflow automations are capped by plan tier: Basic = 10, Standard = 20, Pro = 50 (SuperERP is unlimited). When you hit the cap, creating a new workflow returns 'Workflow limit reached for your plan' — upgrade to add more.",
    keywords: ["workflow limit", "how many workflows", "cap", "10", "20", "50", "upgrade"],
  },

  // ── Modules & Sections ───────────────────────────────────────────────
  {
    id: "mod-sales",
    category: "Modules & Sections",
    question: "What is the Sales module?",
    answer:
      "Sales handles customer invoices, sales returns and customer accounts. Create an invoice, record payment, print/share the voucher, and view sales records & reports. Customers are debtor accounts with an auto-generated customer code.",
    keywords: ["sales", "invoice", "customer", "sales return", "voucher"],
  },
  {
    id: "mod-quotation",
    category: "Modules & Sections",
    question: "What is the Quotation module?",
    answer:
      "Quotations are pre-sale price offers to a customer. A quotation can be converted into a Sales Order or directly into a Sales Invoice once accepted.",
    keywords: ["quotation", "quote", "estimate", "convert to sales"],
  },
  {
    id: "mod-order",
    category: "Modules & Sections",
    question: "What is the Order module?",
    answer:
      "Orders cover Sales Orders and Purchase Orders — commitments before the actual invoice/bill. A Sales Order becomes a Sales Invoice; a Purchase Order becomes a Purchase Bill.",
    keywords: ["order", "sales order", "purchase order", "PO", "SO"],
  },
  {
    id: "mod-purchase",
    category: "Modules & Sections",
    question: "What is the Purchase module?",
    answer:
      "Purchasing manages supplier (vendor) bills, purchase returns and vendor accounts. Record what you buy, track due amounts, and run purchase reports.",
    keywords: ["purchase", "bill", "vendor", "supplier", "purchase return"],
  },
  {
    id: "mod-inventory",
    category: "Modules & Sections",
    question: "What is the Inventory module?",
    answer:
      "Inventory tracks stock by item/warehouse, with stock adjustments and stock transfers between warehouses. It also drives item availability on sales/purchase.",
    keywords: ["inventory", "stock", "warehouse", "adjustment", "transfer", "items"],
  },
  {
    id: "mod-manufacturing",
    category: "Modules & Sections",
    question: "What is the Manufacturing module? (Standard/Pro)",
    answer:
      "Manufacturing creates production orders that consume raw-material stock and produce finished goods. Available on ERP Standard and Pro plans.",
    keywords: ["manufacturing", "production", "bom", "make"],
  },
  {
    id: "mod-accounts",
    category: "Modules & Sections",
    question: "What is the Accounts module?",
    answer:
      "Accounting holds journals, payments, expenses and the ledger. Every sale/purchase posts here; you can also add manual journal entries and view financial reports (P&L, balances).",
    keywords: ["accounts", "accounting", "journal", "payment", "expense", "ledger", "p&l"],
  },
  {
    id: "mod-hr",
    category: "Modules & Sections",
    question: "What is the HR & Payroll module?",
    answer:
      "HR & Payroll manages employees, attendance, leave requests, shifts and payroll runs. Document expiries (visa, contract, etc.) generate reminders.",
    keywords: ["hr", "payroll", "employee", "attendance", "leave", "salary"],
  },
  {
    id: "mod-service",
    category: "Modules & Sections",
    question: "What is the Service module?",
    answer:
      "Service manages service jobs/tickets and any service expenses billed to customers.",
    keywords: ["service", "job", "ticket", "service expense"],
  },
  {
    id: "mod-crm",
    category: "Modules & Sections",
    question: "What is the CRM module? (Pro)",
    answer:
      "CRM tracks leads, deals and follow-up tasks with a pipeline view. Today's Tasks and call/follow-up reminders live here. Available on ERP Pro.",
    keywords: ["crm", "lead", "deal", "pipeline", "task", "follow up"],
  },
  {
    id: "mod-workflows",
    category: "Modules & Sections",
    question: "What is the Workflows section?",
    answer:
      "Workflows automate approvals and actions when records are created/updated. It has three tabs: Running (live instances), My Approvals (tasks waiting on you), and Configure (define workflows and triggers).",
    keywords: ["workflow", "automation", "approval", "running", "my approvals", "configure"],
  },
  {
    id: "mod-pos",
    category: "Modules & Sections",
    question: "What is POS?",
    answer:
      "Point of Sale is a fast counter-sales screen for quick invoicing and payment, ideal for retail/walk-in customers.",
    keywords: ["pos", "point of sale", "counter", "retail"],
  },
  {
    id: "mod-reports",
    category: "Modules & Sections",
    question: "What does the Reports module offer?",
    answer:
      "Reports include sales/purchase records, customer & supplier ledgers, balances, profit & loss, inventory/stock reports and more. Most support date and branch filters and CSV export.",
    keywords: ["reports", "ledger", "balance", "profit loss", "export", "csv"],
  },

  // ── Fields Glossary ──────────────────────────────────────────────────
  {
    id: "fld-acccode",
    category: "Fields Glossary",
    question: "What is Customer Code (acc_code)?",
    answer:
      "A unique code for a customer account, auto-generated from your company's prefix (default 'CMR') plus a running number, e.g. CMR001. It is required when creating a customer and is filled in automatically.",
    keywords: ["customer code", "acc_code", "cmr", "prefix"],
  },
  {
    id: "fld-accname",
    category: "Fields Glossary",
    question: "What is Account Name / Account?",
    answer:
      "The party on a transaction — a customer (debtor) on sales, a supplier (creditor) on purchases, or a ledger account for journals/payments.",
    keywords: ["account", "acc_name", "party", "debtor", "creditor"],
  },
  {
    id: "fld-branch",
    category: "Fields Glossary",
    question: "What is Branch?",
    answer:
      "A location/unit within your company. Records belong to a branch and reports are scoped to the active branch. Your subscription belongs to the company (institution) that owns the branches.",
    keywords: ["branch", "location", "institution", "company"],
  },
  {
    id: "fld-voucher",
    category: "Fields Glossary",
    question: "What is a Voucher / Voucher No?",
    answer:
      "A voucher is a saved transaction document (invoice, bill, payment, etc.). The voucher number is its human-readable reference used for lookups and printing.",
    keywords: ["voucher", "voucher no", "invoice number", "document"],
  },
  {
    id: "fld-due",
    category: "Fields Glossary",
    question: "What is Due / Due Amount and Due Date?",
    answer:
      "Due Amount is the unpaid balance on an invoice/bill. On workflow tasks, the Due date is the deadline for an approval before it escalates.",
    keywords: ["due", "balance", "outstanding", "due date", "deadline"],
  },
  {
    id: "fld-status",
    category: "Fields Glossary",
    question: "What do task statuses mean (Created/Pending/Approved/Rejected/Completed/Cancelled)?",
    answer:
      "Created = step not yet reached; Pending = waiting on a decision; Approved/Rejected = the decision taken; Completed = automatic step finished; Cancelled = dropped because the workflow was rejected upstream.",
    keywords: ["status", "pending", "approved", "rejected", "completed", "cancelled", "created"],
  },

  // ── Common Flows ─────────────────────────────────────────────────────
  {
    id: "flow-customer",
    category: "Common Flows",
    question: "How do I create a customer?",
    answer:
      "Sales → Customers → New. The Customer Code auto-fills (e.g. CMR001). Enter name and contact details and Save. If the code doesn't fill or saving fails with a branch error, log out and back in (your session branch may be stale).",
    keywords: ["create customer", "new customer", "add customer", "customer flow"],
  },
  {
    id: "flow-invoice",
    category: "Common Flows",
    question: "How do I create a sales invoice?",
    answer:
      "Sales → New Sales. Pick the customer, add items (qty, rate, tax/discount), set the payment, and Save. The invoice posts to accounts and updates stock; you can then print or share the voucher.",
    keywords: ["create invoice", "new sale", "sales flow", "bill customer"],
  },
  {
    id: "flow-purchase",
    category: "Common Flows",
    question: "How do I record a purchase bill?",
    answer:
      "Purchase → New Purchase. Choose the supplier, add purchased items, set tax/payment and Save. Stock increases and a payable is recorded against the supplier.",
    keywords: ["purchase flow", "buy", "supplier bill", "record purchase"],
  },
  {
    id: "flow-quote-to-sale",
    category: "Common Flows",
    question: "How does a quotation become a sale?",
    answer:
      "Create a Quotation → once accepted, use 'Convert to Sales' (or convert to a Sales Order first). The line items carry over so you don't re-enter them.",
    keywords: ["convert quotation", "quote to sale", "quotation flow"],
  },
  {
    id: "flow-print",
    category: "Common Flows",
    question: "How do I view/print an invoice voucher?",
    answer:
      "Open the sale (Sales Record or a record link) — the voucher opens at /print/sales/<id>. Use your browser print, or the on-screen actions. The voucher only loads for sales in your active branch.",
    keywords: ["print invoice", "voucher view", "reprint", "share invoice"],
  },

  // ── Workflows ────────────────────────────────────────────────────────
  {
    id: "wf-setup",
    category: "Workflows",
    question: "How do I set up a workflow?",
    answer:
      "Workflows → Configure → Definitions → New workflow. Pick the module, set the trigger event (e.g. On record create), and define the graph (start → condition/action → end). Then add a Trigger (Configure → Triggers) linking the module+event to the workflow. A workflow only fires when an enabled trigger points at it.",
    keywords: ["create workflow", "setup workflow", "definition", "trigger", "configure"],
  },
  {
    id: "wf-assign",
    category: "Workflows",
    question: "Why is a workflow task 'not assigned to anyone'?",
    answer:
      "An 'Assign to User' step needs a target user in the node (data.target), and 'Assign to Role' needs a role. If it's empty, the task is created but unassigned. Edit the workflow graph and set the target.",
    keywords: ["unassigned", "assign to user", "assign to role", "target", "no assignee"],
  },
  {
    id: "wf-approve",
    category: "Workflows",
    question: "How do I approve or reject a task?",
    answer:
      "Workflows → My Approvals (or the bell notification → it deep-links here). Click Approve/Reject and optionally add a remark. Approving resumes the workflow; rejecting cancels the remaining steps.",
    keywords: ["approve", "reject", "my approvals", "decision", "remark"],
  },
  {
    id: "wf-notify",
    category: "Workflows",
    question: "What workflow notifications will I get?",
    answer:
      "You'll be notified when a workflow is triggered by your record, when a task is assigned to you (links to My Approvals), and when your request is approved/rejected. Once you action a task, its 'action needed' notification is deactivated.",
    keywords: ["notification", "bell", "triggered", "assigned", "approved", "alert"],
  },
  {
    id: "wf-actions",
    category: "Workflows",
    question: "What actions can a workflow step do?",
    answer:
      "Send Email, Send Notification, Assign to User, Assign to Role (approval steps that pause until decided), and Update Field (set a column on the record). Condition nodes branch yes/no on field comparisons.",
    keywords: ["actions", "send email", "update field", "condition", "assign"],
  },

  // ── Account & Access ─────────────────────────────────────────────────
  {
    id: "acc-password",
    category: "Account & Access",
    question: "How do I change my password?",
    answer:
      "User menu (top-right) → Change Password. Enter your current password and a new one (8–16 characters meeting the strength rules).",
    keywords: ["password", "change password", "reset"],
  },
  {
    id: "acc-login-history",
    category: "Account & Access",
    question: "What is Login History?",
    answer:
      "User menu → Login History shows your recent sign-ins (device, browser, time) so you can spot unfamiliar access.",
    keywords: ["login history", "sessions", "sign in", "devices"],
  },
  {
    id: "acc-denied",
    category: "Account & Access",
    question: "I get 'User Access Denied To This Module'. Why?",
    answer:
      "Your subscription plan (or role) doesn't include that module's tab. Upgrade the plan or have an admin grant your role access to that tab.",
    keywords: ["access denied", "permission", "module access", "forbidden"],
  },
];

export default faqData;
